export interface TrialState {
  /**
   * Whole calendar days remaining (local time), never negative.
   * 0 = the trial ends later today, or has already ended.
   */
  daysLeft: number;
  /** True once trialEndsAt is in the past. */
  expired: boolean;
}

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Single owner for "how many trial days are left" (T-444). Counts CALENDAR days
 * (local time): a trial ending later today reads as "ends today" (daysLeft 0,
 * not expired) rather than "1 day left"; one ending tomorrow reads 1, etc.
 * Returns null when the subscription is not a trial or has no end date.
 */
export function getTrialState(
  status: string | null | undefined,
  trialEndsAt: string | Date | null | undefined,
  now: Date = new Date(),
): TrialState | null {
  if (status !== 'TRIAL' || !trialEndsAt) return null;
  const endDate = new Date(trialEndsAt);
  if (Number.isNaN(endDate.getTime())) return null;
  if (endDate.getTime() <= now.getTime()) return { daysLeft: 0, expired: true };
  const days = Math.round((startOfLocalDay(endDate) - startOfLocalDay(now)) / 86_400_000);
  return { daysLeft: Math.max(0, days), expired: false };
}

export type TrialPhase = 'live' | 'grace' | 'expired';

export interface TrialPhaseState {
  phase: TrialPhase;
  /** Calendar days left of the live trial; 0 in grace and expired. */
  daysLeft: number;
  /** End of the full-access grace week. ALWAYS the server's date; never computed here (POS-I234). */
  graceEndsAt: Date | null;
}

/**
 * Phase of a trial for the banners and badges: live, grace (trial end passed, full access
 * continues) or expired (read-only until a plan is assigned). The calendar-day maths stays in
 * getTrialState; the grace end and the expired phase come from the server (auth/me,
 * subscription/me), so the frontend never adds 7 days itself.
 */
export function getTrialPhaseState(
  status: string | null | undefined,
  trialEndsAt: string | Date | null | undefined,
  now: Date = new Date(),
  server?: { trialPhase?: string | null; graceEndsAt?: string | Date | null } | null,
): TrialPhaseState | null {
  const grace = server?.graceEndsAt ? new Date(server.graceEndsAt) : null;
  const graceEndsAt = grace && !Number.isNaN(grace.getTime()) ? grace : null;
  if (server?.trialPhase === 'expired') return { phase: 'expired', daysLeft: 0, graceEndsAt };
  const base = getTrialState(status, trialEndsAt, now);
  if (!base) return null;
  return base.expired
    ? { phase: 'grace', daysLeft: 0, graceEndsAt }
    : { phase: 'live', daysLeft: base.daysLeft, graceEndsAt };
}
